import { useState } from 'react';
import { uploadProductImage } from '../api/uploadApi';
import { compressImage } from '../utils/image';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

/** `acceptVideo` é exclusivo do banner — hoje o único upload cujo backend
 *  aceita `video/mp4`/`video/webm` além de imagem/GIF. `compressImage` já
 *  devolve vídeos intactos (só recomprime `image/*`), então não precisa de
 *  tratamento especial aqui além de deixar passar pela validação. */
function useImageUpload(uploadFn = uploadProductImage, { acceptVideo = false } = {}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  async function upload(file) {
    const isImage = file.type.startsWith('image/');
    const isVideo = acceptVideo && file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      setError(
        acceptVideo
          ? 'Selecione uma imagem (JPG, PNG, WEBP ou GIF) ou um vídeo (MP4 ou WEBM).'
          : 'Selecione um arquivo de imagem (JPG, PNG, WEBP ou GIF).'
      );
      return null;
    }

    setUploading(true);
    setError(null);
    try {
      // Antes de medir: a foto de um celular passa fácil dos 5MB, e recusá-la
      // por isso seria recusar justamente o caminho mais natural de cadastrar
      // um produto. O limite continua valendo para o que não dá para reduzir.
      const image = await compressImage(file);
      if (image.size > MAX_IMAGE_SIZE) {
        setError('Imagem muito grande. O tamanho máximo é 5MB.');
        return null;
      }
      return await uploadFn(image);
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setUploading(false);
    }
  }

  return { upload, uploading, error, setError };
}

export default useImageUpload;
